import { useEffect, useRef, useState, Fragment } from "react";
import api from "@/lib/api";

const ORIGIN = typeof window !== "undefined" ? window.location.origin : "";
const HERO_X = 0.9;
const abs = (u) => (u ? (u.startsWith("http") ? u : ORIGIN + u) : "");

function HpBar({ hp, max, color = "#22c55e", w = 56 }) {
  const pct = Math.max(0, Math.min(100, (hp / Math.max(1, max)) * 100));
  return (
    <div style={{ width: w }} className="h-1.5 rounded-full bg-black/60 border border-white/20 overflow-hidden">
      <div className="h-full rounded-full transition-[width] duration-300" style={{ width: `${pct}%`, background: color }} />
    </div>
  );
}

export default function RpgBattle() {
  const [snap, setSnap] = useState(null);
  const offset = useRef(0); // serverSec - clientSec
  const [, setFrame] = useState(0);

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get("/rpg/state");
        if (!alive) return;
        offset.current = data.server_now - Date.now() / 1000;
        setSnap(data);
      } catch (e) {}
    };
    poll();
    const t = setInterval(poll, 1000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  useEffect(() => {
    const t = setInterval(() => setFrame((n) => (n + 1) % 100000), 60);
    return () => clearInterval(t);
  }, []);

  const prevBoss = useRef(false);
  const [intro, setIntro] = useState(false);
  const roar = () => {
    try {
      const C = window.AudioContext || window.webkitAudioContext;
      const ctx = new C();
      if (ctx.state === "suspended") ctx.resume();
      const o = ctx.createOscillator(), g = ctx.createGain();
      o.type = "sawtooth";
      o.frequency.setValueAtTime(75, ctx.currentTime);
      o.frequency.exponentialRampToValueAtTime(38, ctx.currentTime + 1.2);
      g.gain.setValueAtTime(0.001, ctx.currentTime);
      g.gain.exponentialRampToValueAtTime(0.45, ctx.currentTime + 0.08);
      g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 1.3);
      o.connect(g); g.connect(ctx.destination);
      o.start(); o.stop(ctx.currentTime + 1.35);
    } catch (e) {}
  };
  useEffect(() => {
    const bossNow = !!snap?.boss_active;
    if (bossNow && !prevBoss.current) {
      setIntro(true);
      const url = snap?.boss_sound_url;
      if (url) { try { const a = new Audio(url.startsWith("http") ? url : ORIGIN + url); a.volume = 0.7; a.play().catch(() => {}); } catch (e) { roar(); } }
      else roar();
      setTimeout(() => setIntro(false), 1700);
    }
    prevBoss.current = bossNow;
  }, [snap?.boss_active]);

  const lastDropAt = useRef(0);
  const chime = () => {
    try {
      const C = window.AudioContext || window.webkitAudioContext;
      const ctx = new C();
      if (ctx.state === "suspended") ctx.resume();
      [880, 1320].forEach((f, i) => {
        const o = ctx.createOscillator(), g = ctx.createGain();
        o.type = "triangle";
        o.frequency.setValueAtTime(f, ctx.currentTime + i * 0.12);
        g.gain.setValueAtTime(0.001, ctx.currentTime + i * 0.12);
        g.gain.exponentialRampToValueAtTime(0.4, ctx.currentTime + i * 0.12 + 0.03);
        g.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + i * 0.12 + 0.4);
        o.connect(g); g.connect(ctx.destination);
        o.start(ctx.currentTime + i * 0.12); o.stop(ctx.currentTime + i * 0.12 + 0.45);
      });
    } catch (e) {}
  };
  useEffect(() => {
    const drops = (snap?.fx || []).filter((f) => f.kind === "supply_drop");
    if (!drops.length) return;
    const newest = Math.max(...drops.map((f) => f.at || 0));
    if (newest > lastDropAt.current) {
      lastDropAt.current = newest;
      const url = snap?.supply_drop_sound_url;
      if (url) { try { const a = new Audio(url.startsWith("http") ? url : ORIGIN + url); a.volume = 0.75; a.play().catch(() => {}); } catch (e) { chime(); } }
      else chime();
    }
  }, [snap?.fx]);

  if (!snap || !snap.running) return null;
  const nowSec = Date.now() / 1000 + offset.current;
  const hero = snap.hero;
  const CLASS_X = { tank: 0.70, assassin: snap.melee ? 0.72 : 0.76, archer: 0.80, cleric: 0.85 };
  const xOf = (e) => Math.max(0, Math.min(e.cap ?? 0.82, (nowSec - e.spawned_at) / Math.max(0.1, e.walk_sec)));
  const downSecs = !hero.alive ? Math.max(0, Math.ceil(hero.down_until - nowSec)) : 0;

  // group viewers by class
  const groups = {};
  const rOrder = { "": -1, common: 0, rare: 1, legendary: 2 };
  (snap.viewers || []).forEach((v) => {
    const g = (groups[v.cls] = groups[v.cls] || { cls: v.cls, emoji: v.emoji, image_url: v.image_url, attack_url: v.attack_url, members: [], glow: "" });
    g.members.push(v);
    if ((rOrder[v.glow] ?? -1) > (rOrder[g.glow] ?? -1)) g.glow = v.glow || "";
    g.charge = Math.max(g.charge || 0, v.charge || 0);
  });
  const groupList = Object.values(groups);

  const FX = {
    aoe: { e: "🏹", color: "#38bdf8" }, front: { e: "💥", color: "#f59e0b" },
    execute: { e: "🗡️", color: "#ef4444" }, shield: { e: "🛡️", color: "#60a5fa" }, smite: { e: "⚡", color: "#fde047" },
    special: { e: "✨", color: "#c084fc" }, levelup: { e: "⭐", color: "#fbbf24" },
  };

  return (
    <div className={`absolute inset-0 z-20 pointer-events-none select-none ${intro ? "rpg-shake" : ""}`} data-testid="overlay-rpg">
      <style>{`@keyframes rpgShake{0%,100%{transform:translate(0,0)}10%{transform:translate(-8px,4px)}20%{transform:translate(7px,-5px)}30%{transform:translate(-6px,3px)}40%{transform:translate(6px,4px)}50%{transform:translate(-5px,-3px)}60%{transform:translate(5px,3px)}70%{transform:translate(-4px,2px)}80%{transform:translate(4px,-2px)}90%{transform:translate(-2px,1px)}}.rpg-shake{animation:rpgShake .5s linear 3}@keyframes fxPop{0%{opacity:0;transform:translate(-50%,10px) scale(.6)}20%{opacity:1;transform:translate(-50%,-6px) scale(1.15)}100%{opacity:0;transform:translate(-50%,-70px) scale(1)}}@keyframes chargeAura{0%{opacity:.9;transform:translate(-50%,-50%) scale(.6)}100%{opacity:0;transform:translate(-50%,-50%) scale(2.1)}}@keyframes chargeGlow{0%,100%{filter:drop-shadow(0 0 6px #fde047)}50%{filter:drop-shadow(0 0 16px #fde047) drop-shadow(0 0 6px #f59e0b)}}@keyframes dangerPulse{0%,100%{box-shadow:0 0 12px rgba(239,68,68,.5);border-color:rgba(239,68,68,.7)}50%{box-shadow:0 0 26px rgba(239,68,68,.95);border-color:rgba(239,68,68,1)}}@keyframes rallyBlink{0%,100%{opacity:1}50%{opacity:.35}}`}</style>

      {/* Boss intro */}
      {intro && (
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-40" data-testid="rpg-boss-intro">
          <div className="px-8 py-3 rounded-2xl text-3xl font-800 text-white animate-pulse" style={{ background: "linear-gradient(90deg,#7f1d1d,#ff3366,#7f1d1d)", fontFamily: "Outfit, sans-serif", textShadow: "0 0 18px #ff3366", boxShadow: "0 0 50px rgba(255,51,102,0.7)" }}>
            ⚠️ BOSS INCOMING ⚠️
          </div>
        </div>
      )}

      {/* Ability FX bursts + legendary loot toast */}
      {(snap.fx || []).map((f) => {
        const age = nowSec - f.at;
        // atk (projectiles) and poison_tick have their own dedicated renderers below — skip here
        if (f.kind === "atk" || f.kind === "poison_tick") return null;
        if (f.kind === "princess_down") {
          if (age < 0 || age > 3.2) return null;
          return (
            <div key={f.user + f.at} className="absolute left-1/2 top-1/4 -translate-x-1/2 z-50 text-center" style={{ animation: "fxPop 3.2s ease-out forwards" }} data-testid="rpg-princess-down">
              <div className="text-5xl font-800" style={{ color: "#ff3366", textShadow: "0 0 22px #ff3366, 0 0 8px #ff0033", fontFamily: "Outfit, sans-serif" }}>👸💔 THE PRINCESS FELL!</div>
              <div className="text-lg text-pink-200 mt-1">The dungeon resets to Wave 1…</div>
            </div>
          );
        }
        if (f.kind === "legendary") {
          if (age < 0 || age > 2.4) return null;
          return (
            <div key={f.user + f.at} className="absolute left-1/2 top-1/3 -translate-x-1/2 z-40 text-center" style={{ animation: "fxPop 2.4s ease-out forwards" }} data-testid="rpg-legendary-toast">
              <div className="text-4xl font-800" style={{ color: "#f59e0b", textShadow: "0 0 18px #fde047, 0 0 6px #f59e0b", fontFamily: "Outfit, sans-serif" }}>✨ LEGENDARY! ✨</div>
              <div className="text-sm text-amber-200 mt-1">{f.emoji} {f.name} → {f.user}</div>
            </div>
          );
        }
        if (f.kind === "supply_drop") {
          if (age < 0 || age > 3.0) return null;
          const tierColor = { common: "#a3e635", rare: "#38bdf8", legendary: "#f59e0b" }[f.tier] || "#a3e635";
          return (
            <div key={"sd" + f.user + f.at} className="absolute left-1/2 top-1/4 -translate-x-1/2 z-40 text-center" style={{ animation: "fxPop 3s ease-out forwards" }} data-testid="rpg-supply-drop-toast">
              <div className="px-6 py-2 rounded-2xl text-2xl font-800 text-white" style={{ background: "linear-gradient(90deg,rgba(10,10,10,.9),rgba(20,20,20,.9))", border: `2px solid ${tierColor}`, boxShadow: `0 0 30px ${tierColor}`, fontFamily: "Outfit, sans-serif", textShadow: `0 0 12px ${tierColor}` }}>
                📦 SUPPLY DROP from {f.user}!
              </div>
              <div className="text-xs mt-1 font-700 uppercase tracking-widest" style={{ color: tierColor }}>{f.tier} crate incoming</div>
            </div>
          );
        }
        if (age < 0 || age > 1.2) return null;
        const d = FX[f.kind] || FX.front;
        return (
          <div key={f.user + f.at} className="absolute z-30" style={{ left: "50%", bottom: 300, transform: "translateX(-50%)", animation: "fxPop 1.2s ease-out forwards" }}>
            <div className="px-3 py-1 rounded-full text-sm font-800 whitespace-nowrap flex items-center gap-1.5" style={{ background: "rgba(10,10,10,0.85)", border: `1px solid ${d.color}`, color: d.color }}>
              {f.img ? <img src={abs(f.img)} alt="" className="w-5 h-5 object-contain" /> : <span>{d.e}</span>} {f.name}!
            </div>
          </div>
        );
      })}

      {/* Ranged projectiles (arrow / knife / magic — emoji or image/GIF) */}
      {(snap.fx || []).filter((f) => f.kind === "atk" && f.ranged && (f.proj || f.proj_url)).map((f, i) => {
        const dur = 0.55;
        const p = (nowSec - f.at) / dur;
        if (p < 0 || p > 1) return null;
        const fx = f.cls === "hero" ? HERO_X : (CLASS_X[f.cls] ?? 0.8);
        const left = fx + ((f.tx ?? fx) - fx) * p;
        const flip = (f.tx ?? fx) < fx ? -1 : 1;
        const bottom = 5 + Math.sin(p * Math.PI) * 5; // fly along the fighters' bodies (was too high)
        const common = { left: `${left * 100}%`, bottom: `${bottom}%`, zIndex: 30, filter: "drop-shadow(0 0 8px rgba(255,255,255,0.85))" };
        // optional glowing trail behind the projectile (custom GIF or default glow)
        const trailAnchor = flip < 0 ? "translate(0,50%)" : "translate(-100%,50%)"; // sit the trail behind, along travel
        const trailEl = (f.trail || f.trail_url) ? (
          f.trail_url
            ? <img key={"tr" + f.cls + f.at + i} src={abs(f.trail_url)} alt="" data-testid={`rpg-trail-${f.cls}`} className="absolute object-fill pointer-events-none"
                   style={{ left: `${left * 100}%`, bottom: `${bottom}%`, width: 80, height: 34, transform: `${trailAnchor} scaleX(${flip})`, zIndex: 29, opacity: 0.85 }} />
            : <div key={"tr" + f.cls + f.at + i} className="absolute pointer-events-none" data-testid={`rpg-trail-${f.cls}`}
                   style={{ left: `${left * 100}%`, bottom: `${bottom + 1.4}%`, width: 72, height: 9, transform: trailAnchor,
                            background: flip < 0 ? "linear-gradient(90deg, rgba(255,225,160,0.95), rgba(255,180,90,0) 90%)" : "linear-gradient(270deg, rgba(255,225,160,0.95), rgba(255,180,90,0) 90%)",
                            filter: "blur(3px)", borderRadius: 9, zIndex: 29 }} />
        ) : null;
        const projEl = f.proj_url
          ? <img key={"proj" + f.cls + f.at + i} src={abs(f.proj_url)} alt="" data-testid={`rpg-proj-${f.cls}`} className="absolute object-contain"
                 style={{ ...common, width: 40, height: 40, transform: `translate(-50%,0) scaleX(${flip})` }} />
          : <span key={"proj" + f.cls + f.at + i} className="absolute" data-testid={`rpg-proj-${f.cls}`}
                  style={{ ...common, transform: `translate(-50%,0) scaleX(${flip}) rotate(${flip < 0 ? -20 : 20}deg)`, fontSize: 30 }}>{f.proj}</span>;
        return <Fragment key={"pw" + f.cls + f.at + i}>{trailEl}{projEl}</Fragment>;
      })}
      {/* Poison ticks */}
      {(snap.fx || []).filter((f) => f.kind === "poison_tick").map((f, i) => {
        const p = (nowSec - f.at) / 0.9;
        if (p < 0 || p > 1) return null;
        return <span key={"pz" + f.at + i} className="absolute" style={{ left: `${(f.tx ?? 0.5) * 100}%`, bottom: `${20 + p * 8}%`, transform: "translateX(-50%)", opacity: 1 - p, color: "#84cc16", fontSize: 13, fontWeight: 800 }}>☠</span>;
      })}

      <div className="absolute top-20 left-1/2 -translate-x-1/2 flex items-center gap-3 px-4 py-1.5 rounded-full backdrop-blur-md"
           style={{ background: "rgba(10,10,10,0.8)", border: "1px solid rgba(255,51,102,0.5)" }}
           data-testid="rpg-hud">
        <span className="text-[11px] font-800 tracking-widest text-primary" style={{ fontFamily: "Outfit, sans-serif" }}>⚔️ DUNGEON</span>
        <span className="text-xs text-white">Wave <b>{snap.wave}</b></span>
        <span className="text-xs text-white">Kills <b>{snap.kills}</b></span>
        <span className="text-xs text-sky-300">🛡️ {snap.party_alive}/{snap.party}</span>
        <span className="text-xs text-amber-300">✨ {snap.gold}</span>
        {snap.best_wave > 0 && <span className="text-xs text-fuchsia-300" data-testid="rpg-best-wave">🏅 Best W{snap.best_wave}</span>}
        {snap.rally_active && <span className="text-xs font-800 text-orange-400" style={{ animation: "rallyBlink 0.6s infinite" }} data-testid="rpg-rally">⚔️ RALLY!</span>}
      </div>

      {/* Princess HP bar (protect target) */}
      {(() => {
        const pct = Math.max(0, Math.min(100, (hero.hp / Math.max(1, hero.max_hp)) * 100));
        const danger = hero.alive && pct <= 30;
        return (
          <div className="absolute top-2 left-1/2 -translate-x-1/2 w-[420px] max-w-[60vw]" data-testid="rpg-princess-bar">
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="text-xl">{hero.emoji}</span>
              <span className="font-800 tracking-wide text-white" style={{ fontFamily: "Outfit, sans-serif", textShadow: "0 0 12px #ff3366" }}>{hero.name}</span>
              {danger && <span className="text-[10px] font-800 text-red-400" style={{ animation: "rallyBlink 0.5s infinite" }}>⚠ DANGER</span>}
            </div>
            <div className="h-4 rounded-full bg-black/70 border-2 overflow-hidden" style={danger ? { animation: "dangerPulse 0.6s infinite" } : { borderColor: "rgba(34,197,94,0.7)", boxShadow: "0 0 16px rgba(34,197,94,0.4)" }}>
              <div className="h-full transition-[width] duration-300" style={{ width: `${pct}%`, background: danger ? "linear-gradient(90deg,#ef4444,#f97316)" : "linear-gradient(90deg,#22c55e,#a3e635)" }} />
            </div>
            <div className="text-center text-[10px] mt-0.5 font-700" style={{ color: danger ? "#fca5a5" : "#bbf7d0" }}>{Math.max(0, hero.hp)} / {hero.max_hp} · Protect the Princess!</div>
          </div>
        );
      })()}

      {/* Boss health bar + name plate */}
      {(() => {
        const boss = (snap.enemies || []).find((e) => e.kind === "boss");
        if (!boss) return null;
        const pct = Math.max(0, Math.min(100, (boss.hp / Math.max(1, boss.max_hp)) * 100));
        return (
          <div className="absolute top-28 left-1/2 -translate-x-1/2 w-[560px] max-w-[70vw]" data-testid="rpg-boss-bar">
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className="text-2xl animate-pulse">{boss.emoji}</span>
              <span className="font-800 tracking-wide text-white" style={{ fontFamily: "Outfit, sans-serif", textShadow: "0 0 12px #ff3366" }}>{boss.name}</span>
            </div>
            <div className="h-4 rounded-full bg-black/70 border-2 border-red-500/70 overflow-hidden" style={{ boxShadow: "0 0 20px rgba(255,51,102,0.5)" }}>
              <div className="h-full transition-[width] duration-300" style={{ width: `${pct}%`, background: "linear-gradient(90deg,#ff3366,#ff8a00)" }} />
            </div>
            <div className="text-center text-[10px] text-red-200 mt-0.5 font-700">{boss.hp} / {boss.max_hp}</div>
          </div>
        );
      })()}

      {/* Kill feed */}
      <div className="absolute top-40 left-1/2 -translate-x-1/2 flex flex-col items-center gap-0.5">
        {(snap.log || []).slice(0, 3).map((l, i) => (
          <div key={l + i} className="text-[10px] text-white/70 px-2 py-0.5 rounded bg-black/40" style={{ opacity: 1 - i * 0.3 }}>{l}</div>
        ))}
      </div>

      {/* Battle line */}
      <div className="absolute left-0 right-0" style={{ bottom: 118, height: 0 }}>
        {/* Loot crates falling to center */}
        {(snap.crates || []).map((c) => {
          const fallP = Math.max(0, Math.min(1, (nowSec - c.spawned_at) / Math.max(0.1, c.fall_sec)));
          const landed = c.opened || fallP >= 1;
          return (
            <div key={c.id} className="absolute flex flex-col items-center" style={{ left: "45%", bottom: landed ? 10 : `${10 + (1 - fallP) * 520}px`, transform: "translateX(-50%)", transition: "bottom 0.1s linear" }} data-testid="rpg-crate">
              {landed && <div className="text-[10px] font-800 text-amber-300 mb-1 animate-bounce">{c.label}!</div>}
              {c.image_url
                ? <img src={abs(c.image_url)} alt="" className="object-contain" style={{ width: 56, height: 56, filter: landed ? "drop-shadow(0 0 14px #ffd700)" : "none" }} />
                : <span style={{ fontSize: 46, filter: landed ? "drop-shadow(0 0 14px #ffd700)" : "none" }}>{c.emoji}</span>}
            </div>
          );
        })}

        {snap.enemies.map((e) => {
          const x = xOf(e);
          const boss = e.kind === "boss";
          const eAttacking = e.attack_url && e.atk_at && nowSec - e.atk_at >= 0 && nowSec - e.atk_at < 0.35;
          const eSprite = eAttacking ? e.attack_url : e.image_url;
          return (
            <div key={e.id} className="absolute flex flex-col items-center animate-fade-up"
                 style={{ left: `${x * 100}%`, bottom: 0, transform: "translateX(-50%)" }}
                 data-testid={`rpg-enemy-${e.kind}`}>
              <HpBar hp={e.hp} max={e.max_hp} color="#ef4444" w={boss ? 90 : 48} />
              <div className="mt-1" style={{ filter: e.poisoned ? "drop-shadow(0 0 6px #84cc16)" : "drop-shadow(0 2px 4px rgba(0,0,0,0.6))" }}>
                {eSprite
                  ? <img src={abs(eSprite)} alt="" className="object-contain" style={{ width: boss ? 120 : 56, height: boss ? 120 : 56, transform: "scaleX(-1)" }} />
                  : <span style={{ fontSize: boss ? 92 : 46, display: "inline-block", transform: "scaleX(-1)" }}>{e.emoji}</span>}
              </div>
              {boss && <div className="text-[10px] text-red-300 font-700 -mt-1">{e.name}</div>}
            </div>
          );
        })}

        {/* Party — positioned by role: tank front, then assassin, archer, cleric back */}
        {(() => {
          return groupList.map((g) => {
            const hp = g.members.reduce((s, m) => s + m.hp, 0);
            const max = g.members.reduce((s, m) => s + m.max_hp, 0);
            const aliveN = g.members.filter((m) => m.alive).length;
            const gx = CLASS_X[g.cls] ?? 0.78;
            const justFired = (snap.fx || []).some((f) => f.cls === g.cls && nowSec - f.at >= 0 && nowSec - f.at < 0.7);
            const attacking = (snap.fx || []).some((f) => f.kind === "atk" && f.cls === g.cls && nowSec - f.at >= 0 && nowSec - f.at < 0.35);
            const nearFull = (g.charge || 0) >= 0.9;
            const sprite = (attacking && g.attack_url) ? g.attack_url : g.image_url;
            return (
              <div key={g.cls} className="absolute flex flex-col items-center" style={{ left: `${gx * 100}%`, bottom: 0, transform: "translateX(-50%)", transition: "left 0.4s ease" }} data-testid={`rpg-party-${g.cls}`}>
                <HpBar hp={hp} max={max} color="#38bdf8" w={40} />
                <div className="mt-0.5 h-1 rounded-full bg-black/60 overflow-hidden" style={{ width: 40 }} data-testid={`rpg-charge-${g.cls}`}>
                  <div className="h-full rounded-full transition-[width] duration-200" style={{ width: `${(g.charge || 0) * 100}%`, background: g.charge >= 1 ? "#fde047" : "#a855f7" }} />
                </div>
                <div className="relative mt-1" data-testid={`rpg-class-${g.cls}`}>
                  {justFired && <div className="absolute left-1/2 top-1/2 rounded-full pointer-events-none" style={{ width: 46, height: 46, border: "3px solid #fde047", animation: "chargeAura 0.7s ease-out forwards" }} data-testid={`rpg-charge-aura-${g.cls}`} />}
                  <div style={{ filter: aliveN ? (justFired || nearFull ? "drop-shadow(0 0 12px #fde047)" : g.glow === "legendary" ? "drop-shadow(0 0 10px #f59e0b) drop-shadow(0 0 4px #fde047)" : g.glow === "rare" ? "drop-shadow(0 0 8px #38bdf8)" : "none") : "grayscale(1) opacity(0.4)", animation: justFired ? "chargeGlow 0.7s ease-out" : "none" }}>
                    {sprite
                      ? <img src={abs(sprite)} alt="" className="object-contain" style={{ width: 46, height: 46 }} />
                      : <span style={{ fontSize: 38, display: "inline-block" }}>{g.emoji}</span>}
                  </div>
                  {g.members.length > 1 && (
                    <span className="absolute -bottom-1 -right-1 text-[9px] font-800 text-black bg-sky-300 rounded-full px-1 leading-tight">x{g.members.length}</span>
                  )}
                </div>
              </div>
            );
          });
        })()}

        {/* Princess (protect target) */}
        <div className="absolute flex flex-col items-center" style={{ left: `${HERO_X * 100}%`, bottom: 0, transform: "translateX(-50%)" }} data-testid="rpg-hero">
          <div className="mb-0.5 text-[10px] text-primary font-800" style={{ fontFamily: "Outfit, sans-serif" }}>{hero.name}</div>
          <div style={{ filter: !hero.alive ? "grayscale(1) opacity(0.5)" : (hero.hp / Math.max(1, hero.max_hp) <= 0.3 ? "drop-shadow(0 0 12px #ef4444)" : "drop-shadow(0 0 10px rgba(255,51,102,0.55))"), animation: (hero.alive && hero.hp / Math.max(1, hero.max_hp) <= 0.3) ? "rallyBlink 0.6s infinite" : "none" }}>
            {hero.image_url
              ? <img src={abs(hero.image_url)} alt="" className="object-contain animate-bounce" style={{ width: 84, height: 84 }} />
              : <span style={{ fontSize: 72, display: "inline-block" }} className={hero.alive ? "animate-bounce" : ""}>{hero.emoji}</span>}
          </div>
          <div className="mt-1"><HpBar hp={hero.hp} max={hero.max_hp} color="#22c55e" w={90} /></div>
          {!hero.alive && <div className="text-[10px] text-red-400 font-700 mt-0.5">DOWN {downSecs}s</div>}
        </div>
      </div>
    </div>
  );
}
